// ============================================================================
// PURCHASE DEBIT NOTE SERVICE (ADR-0049 Slice 2) — entity + BU scoped.
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { purchaseDebitNoteEndpoints } from '../endpoints/commercial.endpoints';
import { buildEndpointWithQuery, appendRepeatedQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse, BackendPagedResult, PaginatedResponse, ID, DocumentLineLedgerDto,
  PurchaseDebitNoteDto, CreatePurchaseDebitNoteRequest, UpdatePurchaseDebitNoteRequest,
  PurchaseDebitNoteQueryParams, PurchaseDebitNotePrefillDto, SupplierDuplicateProbe,
  PurchaseNoteRegisterRowDto, PurchaseNoteRegisterQueryParams,
} from '../types';

class PurchaseDebitNoteService {
  /** The purchase notes register — our debit notes + Supplier Debit Notes (ADR-0053). */
  async getRegister(params?: PurchaseNoteRegisterQueryParams): Promise<PaginatedResponse<PurchaseNoteRegisterRowDto>> {
    const endpoint = params ? buildEndpointWithQuery(purchaseDebitNoteEndpoints.register(), params) : purchaseDebitNoteEndpoints.register();
    const response = await get<ApiResponse<BackendPagedResult<PurchaseNoteRegisterRowDto>>>(endpoint);
    const paged = response.data.data;
    return {
      success: response.data.success, message: response.data.message,
      data: Array.from(paged.items || []),
      pagination: {
        currentPage: paged.page, pageSize: paged.pageSize, totalPages: paged.totalPages,
        totalItems: paged.totalCount, hasNextPage: paged.hasNext, hasPreviousPage: paged.hasPrevious,
      },
    };
  }
  async getAll(params?: PurchaseDebitNoteQueryParams): Promise<PaginatedResponse<PurchaseDebitNoteDto>> {
    const endpoint = params ? buildEndpointWithQuery(purchaseDebitNoteEndpoints.list(), params) : purchaseDebitNoteEndpoints.list();
    const response = await get<ApiResponse<BackendPagedResult<PurchaseDebitNoteDto>>>(endpoint);
    const paged = response.data.data;
    return {
      success: response.data.success, message: response.data.message,
      data: Array.from(paged.items || []),
      pagination: {
        currentPage: paged.page, pageSize: paged.pageSize, totalPages: paged.totalPages,
        totalItems: paged.totalCount, hasNextPage: paged.hasNext, hasPreviousPage: paged.hasPrevious,
      },
    };
  }
  async getById(id: ID): Promise<PurchaseDebitNoteDto> {
    const response = await get<ApiResponse<PurchaseDebitNoteDto>>(purchaseDebitNoteEndpoints.getById(id));
    return response.data.data;
  }
  /** Remaining value + per-component tax of a bill, with prefilled lines and tax rows. */
  async prefill(invoiceId: ID): Promise<PurchaseDebitNotePrefillDto> {
    const response = await get<ApiResponse<PurchaseDebitNotePrefillDto>>(purchaseDebitNoteEndpoints.prefill(invoiceId));
    return response.data.data;
  }
  /** Duplicate supplier-credit-note-number WARNING probe — never blocks. */
  async duplicateCheck(partyId: ID, supplierCreditNoteNumber: string, excludeId?: ID): Promise<SupplierDuplicateProbe[]> {
    const url = buildEndpointWithQuery(purchaseDebitNoteEndpoints.duplicateCheck(), {
      partyId, supplierCreditNoteNumber, excludeId: excludeId || undefined,
    });
    const response = await get<ApiResponse<SupplierDuplicateProbe[]>>(url);
    return response.data.data || [];
  }
  /** The ledger each returned item credits on the note date (goods ledger, or the editable pre-fill). */
  async getLineLedgers(date: string, itemIds: string[]): Promise<DocumentLineLedgerDto[]> {
    const url = appendRepeatedQuery(buildEndpointWithQuery(purchaseDebitNoteEndpoints.lineLedgers(), { date }), 'itemIds', itemIds);
    const response = await get<ApiResponse<DocumentLineLedgerDto[]>>(url);
    return response.data.data || [];
  }
  async create(data: CreatePurchaseDebitNoteRequest): Promise<PurchaseDebitNoteDto> {
    const response = await post<ApiResponse<PurchaseDebitNoteDto>>(purchaseDebitNoteEndpoints.create(), data);
    return response.data.data;
  }
  async update(id: ID, data: UpdatePurchaseDebitNoteRequest): Promise<PurchaseDebitNoteDto> {
    const response = await put<ApiResponse<PurchaseDebitNoteDto>>(purchaseDebitNoteEndpoints.update(id), data);
    return response.data.data;
  }
  async remove(id: ID, rowVersion: number): Promise<void> {
    const url = buildEndpointWithQuery(purchaseDebitNoteEndpoints.remove(id), { rowVersion });
    await del<ApiResponse<unknown>>(url);
  }
}

export const purchaseDebitNoteService = new PurchaseDebitNoteService();
export default purchaseDebitNoteService;
